import { AlertTriangle, Bell, ChevronDown } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * One notification (ACL-506), drawn the same way wherever it is: the bell's list, a pinned
 * callback, the phone's lead card. Anything that has something to tell somebody passes what it
 * says and this draws it; nothing about appointments or callbacks is known here.
 *
 *     <Notification id={7} title="Callback in 5 min" message="Gil Tan · 3:00 PM PDT"
 *       details={["Purpose: Signing"]} href="/contacts/…" links={[{ label: "Open conversation", href }]} />
 *
 * The title is the way to `href` and is stretched over the whole card, so a press anywhere on it
 * opens it; the links under it are drawn over the stretch and pressed for themselves. A card with
 * no `href` is text. `unread` lights it until the person has seen it; `tone="warning"` is for
 * something that changed under them (a callback handed to somebody else).
 *
 * `foldDetails` (the bell's list, ACL-594) keeps the details out of sight until the card is
 * pressed: then what is stretched over the card is the button that shows and hides them, and the
 * title is a link pressed for itself, as the links are. A card with no details is drawn as above.
 */

export interface NotificationLinkItem {
  label: string;
  href: string;
}

export interface NotificationProps {
  /** What the notification is, for a list's key and for tests (`data-notification`). */
  id?: string | number;
  title: React.ReactNode;
  message?: React.ReactNode;
  /** A few more lines: what it is for, a note, who places the call. */
  details?: readonly React.ReactNode[];
  /** The details shown only once the card is pressed, so a list of them can be read at a glance. */
  foldDetails?: boolean;
  /** When it came, small at the title's end: "2 min ago". */
  time?: React.ReactNode;
  /** A live line under the message: "Starts in 4 min". */
  countdown?: React.ReactNode;
  /** What it is about, in a word, when that no longer stands: "Cancelled". */
  status?: string | null;
  tone?: "info" | "warning";
  /** Not seen yet: lit. */
  unread?: boolean;
  /** Where a press on it goes; none: it is text. */
  href?: string | null;
  links?: readonly NotificationLinkItem[];
  /** The person followed it or one of its links: a popover around it closes itself. */
  onNavigate?: () => void;
  /** In place of the tone's bell or warning sign. */
  icon?: React.ReactNode;
  /** `li` in a list, `div` on its own. */
  as?: "li" | "div";
  /** Tighter, for a narrow place such as the phone's card. */
  compact?: boolean;
  className?: string;
}

export function Notification({
  id, title, message, details = [], foldDetails = false, time, countdown, status, tone = "info",
  unread = false, href, links = [], onNavigate, icon, as: Root = "div", compact = false, className,
}: NotificationProps) {
  const mark = icon ?? (tone === "warning" ? <AlertTriangle aria-hidden /> : <Bell aria-hidden />);
  const folds = foldDetails && details.length > 0;
  const [unfolded, setUnfolded] = React.useState(false);
  const detailsId = React.useId();
  const showDetails = details.length > 0 && (!folds || unfolded);
  return (
    <Root
      data-notification={id}
      className={cn(
        "relative flex gap-2.5",
        compact ? "px-3 py-2" : "px-4 py-2.5",
        folds
          ? "hover:bg-surface-hover has-[button:focus-visible]:bg-surface-hover"
          : href && "has-[a:hover]:bg-surface-hover has-[a:focus-visible]:bg-surface-hover",
        folds && unfolded && "bg-surface-hover",
        unread && (tone === "warning" ? "bg-warning-2" : "bg-accent-2"),
        className,
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center [&>svg]:size-3.5",
          tone === "warning" ? "text-warning-11" : "text-accent-11",
        )}
      >
        {mark}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 text-sm font-medium text-text">
            {href ? (
              // Stretched over the card, so a press anywhere on it opens it; when the card folds,
              // the fold's button is stretched instead and this is drawn over it.
              <Link
                to={href}
                className={cn(folds ? "relative z-[1]" : "after:absolute after:inset-0", "hover:underline")}
                onClick={onNavigate}
              >
                {title}
              </Link>
            ) : (
              title
            )}
          </span>
          {time && <span className="shrink-0 text-xs tabular text-text-subtle">{time}</span>}
        </div>
        {message && <p className="text-xs text-text">{message}</p>}
        {(countdown || status) && (
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            {status ? <Badge tone="danger" size="sm">{status}</Badge> : countdown}
          </p>
        )}
        {showDetails && (
          <ul
            id={detailsId}
            className={cn(
              "flex flex-col gap-0.5 text-xs text-text-muted",
              folds && "my-1 border-l-2 border-border-strong pl-2",
            )}
          >
            {details.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}
        {links.length > 0 && (
          <div className="flex flex-wrap gap-x-3">
            {links.map((link) => (
              // `relative`: over the card's stretched link or button, so it is pressed for itself.
              <Link
                key={link.href}
                to={link.href}
                className="relative z-[1] w-fit text-xs text-accent-11 hover:underline"
                onClick={onNavigate}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
      {folds && (
        // Stretched over the card, so a press anywhere on it but its links shows the details.
        <button
          type="button"
          aria-expanded={unfolded}
          aria-controls={unfolded ? detailsId : undefined}
          aria-label={unfolded ? "Hide details" : "Show details"}
          onClick={() => setUnfolded((was) => !was)}
          className={cn(
            "flex size-5 shrink-0 items-center justify-center self-center rounded text-text-subtle",
            "after:absolute after:inset-0 focus-visible:outline-none",
          )}
        >
          <ChevronDown className={cn("size-4 transition-transform", unfolded && "rotate-180")} aria-hidden />
        </button>
      )}
    </Root>
  );
}
